import { DialogTitle, Dialog, DialogContent, Button, DialogActions, Box } from '@mui/material';
import { DateRange } from 'ui/DateRange';
import { FC, useEffect, useState } from 'react';
import { EAllowedDateDirection } from 'ui/DateRange/types';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { isCustomRangeId } from 'services/constants/dates';

interface IDateRangeModalProps {
	open: boolean;
	setClose: () => void;
	selector?: string;
	allowedDateDirection?: EAllowedDateDirection;
}

export const DateRangeModal: FC<IDateRangeModalProps> = ({
	open,
	setClose,
	selector = '',
	allowedDateDirection = EAllowedDateDirection.ALL,
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [startDate, setStartDate] = useState<Date | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);

	useEffect(() => {
		if (!open) return;
		const startDateStr = searchParams.get(`${selector}StartDate`);
		const endDateStr = searchParams.get(`${selector}EndDate`);
		if (startDateStr) {
			setStartDate(DateTime.fromISO(startDateStr).toJSDate());
		} else {
			setStartDate(null);
		}
		if (endDateStr) {
			setEndDate(DateTime.fromISO(endDateStr).toJSDate());
		} else {
			setEndDate(null);
		}
	}, [open, searchParams, selector]);

	const handleSetStartDate = (date: Date | null) => {
		setStartDate(date);
	};

	const handleSetEndDate = (date: Date | null) => {
		setEndDate(date);
	};

	const handleApply = () => {
		setSearchParams((prevParams) => {
			const newSearchParams = new URLSearchParams(prevParams);
			if (startDate) {
				newSearchParams.set(`${selector}StartDate`, DateTime.fromJSDate(startDate).toISODate()!);
			} else {
				newSearchParams.delete(`${selector}StartDate`);
			}
			if (endDate) {
				newSearchParams.set(`${selector}EndDate`, DateTime.fromJSDate(endDate).toISODate()!);
			} else {
				newSearchParams.delete(`${selector}EndDate`);
			}
			return newSearchParams;
		});
		setClose();
	};

	/**
	 * A custom range that was cancelled before any date was picked would stay in the url as
	 * a filter with nothing to show, so the filter itself is dropped together with the dialog.
	 */
	const handleCancel = () => {
		const hasNoRange =
			!searchParams.get(`${selector}StartDate`) && !searchParams.get(`${selector}EndDate`);

		if (hasNoRange && isCustomRangeId(searchParams.get(selector))) {
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete(selector);
			setSearchParams(newSearchParams);
		}
		setClose();
	};

	/** The dates of the range being picked, shown in the title next to `Select Period`. */
	const formatDate = (date: Date | null) =>
		date ? DateTime.fromJSDate(date).toFormat('dd.MM.yyyy') : '...';

	return (
		<Dialog
			open={open}
			onClose={handleCancel}
			sx={{
				'& .MuiDialog-paper': {
					width: '700px',
					maxWidth: '700px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle
				sx={{
					fontSize: '18px',
					fontWeight: 'bold',
					color: '#006293',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 2,
				}}
			>
				Select Period
				{(!!startDate || !!endDate) && (
					<Box component="span" sx={{ fontSize: '16px', fontWeight: 500, whiteSpace: 'nowrap' }}>
						{formatDate(startDate)} - {formatDate(endDate)}
					</Box>
				)}
			</DialogTitle>
			<DialogContent>
				<DateRange
					startDate={startDate}
					endDate={endDate}
					setStartDate={handleSetStartDate}
					setEndDate={handleSetEndDate}
					allowedDateDirection={allowedDateDirection}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel}>Cancel</Button>
				<Button onClick={handleApply}>Apply</Button>
			</DialogActions>
		</Dialog>
	);
};
